/**
 * IONLOOP Assistant — lexical search scoring engine.
 *
 * A lightweight BM25-inspired ranking model over the prepared in-memory
 * search index. Accounts for term frequency, inverse document frequency,
 * field importance, phrase matches, proximity, coverage, category signals,
 * synonyms and conservative typo tolerance.
 *
 * Pure logic — no browser globals, so the same code runs in Node tests and
 * the browser bundle.
 */

var tokenizer = require('./tokenizer');

// ---------------------------------------------------------------------------
// Tunable weights
// ---------------------------------------------------------------------------
var W = {
    title: 6.0,
    heading: 4.5,
    keywords: 3.0,
    description: 2.0,
    text: 1.0,
    links: 0.5,
    category: 3.0,
    categoryExplicit: 3.5,
    phraseTitle: 8.0,
    phraseHeading: 6.0,
    phraseKeyword: 4.0,
    phraseText: 3.0,
    coverage: 3.0,
    proximity: 1.5,
    negative: -6.0,
    intentNavigationLinks: 0.8,
    intentHowToHeadings: 0.6
};

var BM25_K1 = 1.2;
var BM25_B = 0.75;
var PROXIMITY_WINDOW = 18;

var STOPWORDS = new Set([
    'the', 'a', 'an', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
    'to', 'of', 'for', 'in', 'on', 'and', 'or', 'but', 'if', 'then', 'than',
    'can', 'could', 'would', 'should', 'will', 'shall', 'may', 'might', 'must',
    'do', 'does', 'did', 'how', 'what', 'where', 'when', 'why', 'which', 'who',
    'whom', 'whose', 'tell', 'me', 'about', 'with', 'without', 'from', 'into',
    'onto', 'over', 'under', 'between', 'by', 'at', 'as', 'up', 'down', 'out',
    'off', 'this', 'that', 'these', 'those', 'it', 'its', 'them', 'they', 'you',
    'your', 'my', 'we', 'our', 'us', 'i', 'am', 'have', 'has', 'had', 'their',
    'there', 'not', 'no', 'any', 'all', 'some', 'more', 'most', 'other', 'each',
    'also', 'very', 'just', 'only', 'please', 'want', 'need', 'get', 'got',
    'doesnt', 'dont', 'isnt', 'wasnt', 'are', 'im', 'help'
]);

var CATEGORY_SIGNALS = {
    'trading-markets': ['trading', 'trade', 'spot', 'margin', 'futures', 'orders', 'market', 'markets', 'binance', 'tradingview', 'signal', 'signals', 'dashboard', 'convert', 'converter', 'history', 'news', 'constructor'],
    'wallet-payments': ['wallet', 'payment', 'payments', 'pay', 'deposit', 'withdraw', 'transfer', 'crypto', 'credit'],
    'planning-productivity': ['planning', 'calendar', 'diagram', 'sheet', 'spreadsheet', 'planner', 'schedule', 'notes', 'note', 'time', 'tracker', 'appointment'],
    'account-security': ['security', 'password', 'recover', 'recovery', 'login', 'log in', 'sign in', '2fa', 'two factor', 'device', 'encryption', 'authenticator', 'verify'],
    'automation': ['automation', 'automate', 'autopay', 'workflow', 'recurring', 'alerts', 'alert', 'notification', 'cycle'],
    'business-operations': ['business', 'operations', 'erp', 'pos', 'governance', 'sharing', 'team', 'file'],
    'ionloop-id': ['ionloop id', 'identity', 'single sign on', 'id'],
    'getting-started': ['getting started', 'get started', 'start', 'onboarding', 'create account', 'open account', 'download', 'guide', 'introduction'],
    'help-policies': ['help center', 'faq', 'policy', 'policies', 'privacy', 'terms', 'billing', 'cookie', 'refund']
};

// ---------------------------------------------------------------------------
// Levenshtein distance (deterministic typo tolerance)
// ---------------------------------------------------------------------------
function levenshtein(a, b) {
    var m = a.length;
    var n = b.length;
    if (m === 0) { return n; }
    if (n === 0) { return m; }
    var prev = new Array(n + 1);
    var curr = new Array(n + 1);
    var i, j;
    for (j = 0; j <= n; j += 1) { prev[j] = j; }
    for (i = 1; i <= m; i += 1) {
        curr[0] = i;
        for (j = 1; j <= n; j += 1) {
            var cost = a[i - 1] === b[j - 1] ? 0 : 1;
            curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
        }
        for (j = 0; j <= n; j += 1) { prev[j] = curr[j]; }
    }
    return prev[n];
}

function typoDistanceLimit(word) {
    var len = word.length;
    if (len <= 4) { return 0; }       // no fuzzy matching for very short words
    if (len <= 7) { return 1; }
    return 2;                          // len >= 8
}

// ---------------------------------------------------------------------------
// Search index preparation
// ---------------------------------------------------------------------------
function prepareSearchIndex(documents) {
    var entries = [];
    var totalTextLen = 0;
    var documentFreq = Object.create(null);
    var vocab = Object.create(null);

    (documents || []).forEach(function (doc) {
        (doc.sections || []).forEach(function (section) {
            var title = String(doc.title || '').toLowerCase();
            var heading = String(section.heading || '').toLowerCase();
            var text = String(section.text || '').toLowerCase();
            var keywords = (section.keywords || []).concat(doc.keywords || []).join(' ').toLowerCase();
            var description = String(doc.description || '').toLowerCase();
            var links = (section.links || []).map(function (l) { return l.label + ' ' + l.url; }).join(' ').toLowerCase();

            var textTokens = tokenizer.tokenize(text);
            var titleTokens = tokenizer.tokenize(title);
            var headingTokens = tokenizer.tokenize(heading);
            var keywordTokens = tokenizer.tokenize(keywords);
            var descriptionTokens = tokenizer.tokenize(description);
            var linkTokens = tokenizer.tokenize(links);

            // Stemmed copies for matching recall (plural → singular).
            var textTokensStem = textTokens.map(tokenizer.stem);
            var titleTokensStem = titleTokens.map(tokenizer.stem);
            var headingTokensStem = headingTokens.map(tokenizer.stem);
            var keywordTokensStem = keywordTokens.map(tokenizer.stem);
            var descriptionTokensStem = descriptionTokens.map(tokenizer.stem);
            var linkTokensStem = linkTokens.map(tokenizer.stem);

            var termFreq = Object.create(null);
            var positions = Object.create(null);
            textTokensStem.forEach(function (tok, idx) {
                termFreq[tok] = (termFreq[tok] || 0) + 1;
                if (!positions[tok]) { positions[tok] = []; }
                positions[tok].push(idx);
            });
            // Combined token set for document-frequency (stemmed).
            var combined = Object.create(null);
            [textTokensStem, titleTokensStem, headingTokensStem, keywordTokensStem, descriptionTokensStem, linkTokensStem].forEach(function (arr) {
                arr.forEach(function (tok) { combined[tok] = true; });
            });
            Object.keys(combined).forEach(function (tok) {
                documentFreq[tok] = (documentFreq[tok] || 0) + 1;
                vocab[tok] = true;
            });

            entries.push({
                documentId: doc.id,
                sectionId: section.id,
                title: title,
                heading: heading,
                category: doc.category,
                url: doc.url,
                sourceAuthority: section.sourceAuthority || 80,
                canonicalTopic: (section.canonicalTopic || doc.canonicalTopic || '').toLowerCase(),
                titleTokens: titleTokensStem,
                headingTokens: headingTokensStem,
                keywordTokens: keywordTokensStem,
                descriptionTokens: descriptionTokensStem,
                linkTokens: linkTokensStem,
                textTokens: textTokensStem,
                termFreq: termFreq,
                positions: positions,
                textLen: textTokensStem.length,
                fields: {
                    title: title,
                    heading: heading,
                    keywords: keywords,
                    description: description,
                    links: links,
                    text: text
                }
            });
            totalTextLen += textTokens.length;
        });
    });

    var N = entries.length;
    var avgLen = N > 0 ? totalTextLen / N : 1;

    function idf(term) {
        var df = documentFreq[term] || 0;
        return Math.log(1 + (N - df + 0.5) / (df + 0.5));
    }

    // Build candidate vocabulary for typo correction from knowledge tokens.
    var vocabSet = Object.keys(vocab);

    return {
        entries: entries,
        n: N,
        avgLen: avgLen,
        idf: idf,
        vocab: vocabSet,
        documentFreq: documentFreq
    };
}

function fieldHasTerm(fieldText, fieldTokens, term) {
    if (term.indexOf(' ') !== -1) {
        return (' ' + fieldText + ' ').indexOf(' ' + term + ' ') !== -1;
    }
    var stemmed = tokenizer.stem(term);
    return fieldTokens.indexOf(term) !== -1 || fieldTokens.indexOf(stemmed) !== -1;
}

/**
 * Score a single section against the query analysis.
 * Returns { score, breakdown, matchedTerms, matchedFields, exactCount,
 *           anyExact, phraseMatched, termCoverage }
 */
function scoreSection(entry, analysis, stats) {
    var score = 0;
    var breakdown = {};
    var matchedTerms = [];
    var matchedFields = [];
    var exactCount = 0;
    var anyExact = false;
    var phraseMatched = false;

    function add(fieldName, value, qw) {
        if (value <= 0) { return; }
        score += value;
        breakdown[fieldName] = (breakdown[fieldName] || 0) + value;
        if (matchedFields.indexOf(fieldName) === -1) {
            matchedFields.push(fieldName);
        }
    }

    // -- single terms ------------------------------------------------------
    analysis.queryTerms.forEach(function (qt) {
        var idf = stats.idf(qt.text);
        var matchedHere = false;

        if (fieldHasTerm(entry.fields.title, entry.titleTokens, qt.text)) {
            add('title', qt.weight * W.title * idf, qt.weight);
            matchedHere = true;
        }
        if (fieldHasTerm(entry.fields.heading, entry.headingTokens, qt.text)) {
            add('heading', qt.weight * W.heading * idf, qt.weight);
            matchedHere = true;
        }
        if (fieldHasTerm(entry.fields.keywords, entry.keywordTokens, qt.text)) {
            add('keywords', qt.weight * W.keywords * idf, qt.weight);
            matchedHere = true;
        }
        if (fieldHasTerm(entry.fields.description, entry.descriptionTokens, qt.text)) {
            add('description', qt.weight * W.description * idf, qt.weight);
            matchedHere = true;
        }
        if (fieldHasTerm(entry.fields.links, entry.linkTokens, qt.text)) {
            add('links', qt.weight * W.links * idf, qt.weight);
            matchedHere = true;
        }

        var tf = entry.termFreq[qt.text] || 0;
        if (tf > 0) {
            var bm = idf * (tf * (BM25_K1 + 1)) / (tf + BM25_K1 * (1 - BM25_B + BM25_B * entry.textLen / stats.avgLen));
            add('text', qt.weight * W.text * bm, qt.weight);
            matchedHere = true;
        }

        if (matchedHere) {
            if (matchedTerms.indexOf(qt.text) === -1) {
                matchedTerms.push(qt.text);
            }
            if (qt.kind === 'exact') {
                exactCount += 1;
                anyExact = true;
            }
        }
    });

    // -- phrases -----------------------------------------------------------
    analysis.phrases.forEach(function (phrase) {
        var hit = false;
        if ((' ' + entry.fields.title + ' ').indexOf(' ' + phrase.text + ' ') !== -1) {
            add('phraseTitle', phrase.weight * W.phraseTitle);
            hit = true;
        } else if ((' ' + entry.fields.heading + ' ').indexOf(' ' + phrase.text + ' ') !== -1) {
            add('phraseHeading', phrase.weight * W.phraseHeading);
            hit = true;
        } else if ((' ' + entry.fields.keywords + ' ').indexOf(' ' + phrase.text + ' ') !== -1) {
            add('phraseKeyword', phrase.weight * W.phraseKeyword);
            hit = true;
        } else if ((' ' + entry.fields.text + ' ').indexOf(' ' + phrase.text + ' ') !== -1) {
            add('phraseText', phrase.weight * W.phraseText);
            hit = true;
        }
        if (hit) {
            phraseMatched = true;
            if (matchedTerms.indexOf(phrase.text) === -1) {
                matchedTerms.push(phrase.text);
            }
        }
    });

    // -- category signals --------------------------------------------------
    var catHits = analysis.categorySignals instanceof Map
        ? (analysis.categorySignals.get(entry.category) || 0)
        : (analysis.categorySignals.has && analysis.categorySignals.has(entry.category) ? 1 : 0);
    if (catHits > 0) {
        add('category', W.category * catHits);
    }
    if (analysis.explicitCategory && entry.category === analysis.explicitCategory) {
        add('categoryExplicit', W.categoryExplicit);
    }

    // -- proximity ---------------------------------------------------------
    if (analysis.meaningfulTokens.length >= 2) {
        var prox = proximityScore(entry, analysis.meaningfulTokens);
        if (prox > 0) {
            add('proximity', prox);
        }
    }

    // -- coverage ----------------------------------------------------------
    var coverage = 0;
    if (analysis.meaningfulTokens.length > 0) {
        var matchedMeaningful = analysis.meaningfulTokens.filter(function (tok) {
            return matchedTerms.some(function (mt) { return mt === tok || mt.indexOf(tok) === 0; });
        }).length;
        coverage = matchedMeaningful / analysis.meaningfulTokens.length;
    }
    add('coverage', coverage * W.coverage);

    // -- intent adjustments ------------------------------------------------
    if (analysis.intent === 'navigation' && matchedFields.indexOf('links') !== -1) {
        add('intent', W.intentNavigationLinks);
    }
    if (analysis.intent === 'how-to' && /^(how to|getting started|setup|steps?|guide|walkthrough)/i.test(entry.heading)) {
        add('intent', W.intentHowToHeadings);
    }

    // -- negative terms ----------------------------------------------------
    var negativeHit = false;
    analysis.negatives.forEach(function (neg) {
        if (fieldHasTerm(entry.fields.title, entry.titleTokens, neg) ||
            fieldHasTerm(entry.fields.heading, entry.headingTokens, neg)) {
            negativeHit = true;
        }
    });
    if (negativeHit) {
        add('negative', W.negative);
    }

    var termCoverage = analysis.meaningfulTokens.length > 0 ? coverage : 0;
    var synonymOnly = anyExact === false && exactCount === 0 && analysis.queryTerms.length > 0 &&
        matchedTerms.length > 0 && analysis.meaningfulTokens.length > 0;

    return {
        score: score,
        breakdown: breakdown,
        matchedTerms: matchedTerms,
        matchedFields: matchedFields,
        exactCount: exactCount,
        anyExact: anyExact,
        phraseMatched: phraseMatched,
        termCoverage: termCoverage,
        synonymOnly: synonymOnly,
        negativeHit: negativeHit
    };
}

/**
 * Modest boost when multiple query terms occur close together in the text.
 */
function proximityScore(entry, meaningfulTokens) {
    var present = meaningfulTokens.filter(function (tok) {
        return entry.positions[tok] && entry.positions[tok].length > 0;
    });
    if (present.length < 2) {
        return 0;
    }
    var firstPositions = present.map(function (tok) { return entry.positions[tok][0]; })
        .sort(function (a, b) { return a - b; });
    var span = firstPositions[firstPositions.length - 1] - firstPositions[0];
    if (span < PROXIMITY_WINDOW) {
        return W.proximity * (present.length / Math.max(2, meaningfulTokens.length));
    }
    return 0;
}

// ---------------------------------------------------------------------------
// Typo correction against known knowledge vocabulary
// ---------------------------------------------------------------------------
function correctTypoTerm(term, stats) {
    if (term.length <= 4) {
        return null;
    }
    if (stats.vocab.indexOf(term) !== -1) {
        return null;
    }
    var limit = typoDistanceLimit(term);
    if (limit === 0) {
        return null;
    }
    var best = null;
    var bestDist = limit + 1;
    for (var i = 0; i < stats.vocab.length; i += 1) {
        var candidate = stats.vocab[i];
        if (candidate.length < term.length - 2 || candidate.length > term.length + 2) {
            continue;
        }
        var d = levenshtein(term, candidate);
        if (d < bestDist) {
            bestDist = d;
            best = candidate;
        }
        if (d === 1 && bestDist === 1) {
            break;
        }
    }
    if (best && bestDist <= limit) {
        return best;
    }
    return null;
}

module.exports = {
    W: W,
    STOPWORDS: STOPWORDS,
    CATEGORY_SIGNALS: CATEGORY_SIGNALS,
    prepareSearchIndex: prepareSearchIndex,
    scoreSection: scoreSection,
    correctTypoTerm: correctTypoTerm,
    levenshtein: levenshtein
};
